import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { SimpleModal } from '@livable/saas-react-components';
import { Alert, Button, FormControlLabel, MenuItem, Radio, RadioGroup, Switch, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'src/api';
import { useCurrentSubscriptionTier } from 'src/components/SubscriptionTier';
import { useStore } from 'src/stores';
import { announceSetupChange, NO_OPTIONS } from 'src/stores/utilitySetups/companySetupsRead';
import { IntakeAddress } from './IntakeAddress';
import { messageOf, Needs, Tip } from './parts';
import { apPushChoice, draftOf, FREQUENCY_CHOICES, impactLine, isSubmeterSetup, MAIL_CHOICE, MAX_SETUP_NAME_LENGTH, methodChoices, previewFor, sameValues, SOURCE_CHOICES, sourceChoice, SQUARE_FOOTAGE_METHODS, valuesOf } from './setupEditor';
import { dayLabel, frequencyLabel, methodLabel, SETUP_TIPS } from './setupSummary';
import useStyles from './styles';
const PREVIEW_FAILED = "Couldn't check what saving changes.";

// property-service's answer while another change to the company's setups holds its lock.
const SETUP_BUSY = 'setup_busy';
// A move to Utility portal refused because utilities that follow the setup have no portal login yet (LB-6184).
const PORTAL_LOGIN_MISSING = 'portal_login_missing';
const SourceCard = _ref => {
  let choice = _ref.choice,
    selected = _ref.selected,
    locked = _ref.locked,
    needs = _ref.needs,
    classes = _ref.classes;
  return /*#__PURE__*/React.createElement(FormControlLabel, {
    value: choice.value,
    disabled: locked,
    className: "".concat(classes.card, " ").concat(selected ? classes.cardSelected : ''),
    control: /*#__PURE__*/React.createElement(Radio, {
      size: "small"
    }),
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.cardText
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      component: "span",
      className: classes.cardTitle
    }, choice.title), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      component: "span",
      color: "light"
    }, choice.sub), needs ? /*#__PURE__*/React.createElement(Needs, {
      tier: needs,
      className: classes.needs
    }) : null)
  });
};

/**
 * Changes a company setup as a whole (PRD V3, FR-3, FR-5, FR-6; LB-6189): its name, how its bills reach Livable, Bill
 * to residents and the allocation method, the bill frequency and AP Push. Before saving it says, from the server's
 * preview, how many utilities the edit changes and from when: at once when none of them has been billed, otherwise
 * from the next billing month. Renders nothing while isBillingOptionsEnabled is off.
 *
 * `onSaved(result, { appliedNow })` hears a saved edit; `onSavingChange` hears when the save starts and ends, so the
 * page holds its own Save meanwhile. `onLoginsMissing(missing)` hears whether the latest preview, or a save, was
 * refused because utilities that follow the setup have no portal login yet, so the Setup section can offer its utility's
 * login (LB-6191).
 */
export const UtilitySetupEditor = observer(_ref2 => {
  var _useCurrentSubscripti, _useCurrentSubscripti2, _result$impact$hidden, _result$impact, _draft$billSource;
  let setup = _ref2.setup,
    open = _ref2.open,
    onClose = _ref2.onClose,
    onSaved = _ref2.onSaved,
    onSavingChange = _ref2.onSavingChange,
    onLoginsMissing = _ref2.onLoginsMissing;
  const classes = useStyles();
  const store = useStore();
  const enabled = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;
  const isAppfolio = !!store.company.companyFeatureStore.isAppfolio;
  const occupantsOptIn = !!store.company.companyFeatureStore.enableAllocationByNumberOfOccupants;
  const allows = (_useCurrentSubscripti = (_useCurrentSubscripti2 = useCurrentSubscriptionTier()) === null || _useCurrentSubscripti2 === void 0 ? void 0 : _useCurrentSubscripti2.allows) !== null && _useCurrentSubscripti !== void 0 ? _useCurrentSubscripti : null;

  // Where the editor started, kept while it's open: the setup can be read again beside it meanwhile.
  const _useState = useState(() => setup ? draftOf(setup) : null),
    _useState2 = _slicedToArray(_useState, 2),
    baseline = _useState2[0],
    setBaseline = _useState2[1];
  const _useState3 = useState(baseline),
    _useState4 = _slicedToArray(_useState3, 2),
    draft = _useState4[0],
    setDraft = _useState4[1];
  // { key, state: 'loading' | 'ready' | 'refused' | 'failed', result, message }: the last preview, for the values `key` names.
  const _useState5 = useState({
      key: null,
      state: 'loading',
      result: null,
      message: ''
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    preview = _useState6[0],
    setPreview = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    saving = _useState8[0],
    setSaving = _useState8[1];
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    saveError = _useState0[0],
    setSaveError = _useState0[1];

  // Previews can overlap as values change, and only the latest may answer.
  const latestPreview = useRef(0);
  // A second click before Save re-renders disabled would send the edit twice.
  const savingNow = useRef(false);
  const loginMissing = useRef(onLoginsMissing);
  loginMissing.current = onLoginsMissing;
  const refusedForLogins = err => {
    var _err$response;
    return (err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 || (_err$response = _err$response.data) === null || _err$response === void 0 || (_err$response = _err$response.Error) === null || _err$response === void 0 ? void 0 : _err$response.ErrorCode) === PORTAL_LOGIN_MISSING;
  };
  const setupId = setup === null || setup === void 0 ? void 0 : setup.id;
  const setupName = setup === null || setup === void 0 ? void 0 : setup.name;
  useEffect(() => {
    if (!open || !setup) return;
    const start = draftOf(setup);
    setBaseline(start);
    setDraft(start);
    setSaveError('');
    // The Setup section mounts the editor only while it's open, so this matters to a screen that keeps it mounted
    // (LB-6193's setups list): each opening starts again, while a read of the same setup beside it changes nothing the
    // user is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setupId]);
  const runPreview = useCallback(async key => {
    const call = ++latestPreview.current;
    setPreview({
      key,
      state: 'loading',
      result: null,
      message: ''
    });
    try {
      var _loginMissing$current;
      // The setup's own name: the preview checks the name too, and the one being typed may not be valid yet.
      const resp = await api.UtilitySetups.editSetup({
        setupId,
        draft: _objectSpread(_objectSpread({}, JSON.parse(key)), {}, {
          name: setupName
        }),
        preview: true
      }, NO_OPTIONS);
      if (call !== latestPreview.current) return;
      if (!(resp !== null && resp !== void 0 && resp.response)) throw new Error('No preview answer');
      setPreview({
        key,
        state: 'ready',
        result: resp.response,
        message: ''
      });
      (_loginMissing$current = loginMissing.current) === null || _loginMissing$current === void 0 || _loginMissing$current.call(loginMissing, false);
    } catch (err) {
      var _loginMissing$current2, _err$response2, _err$response3;
      if (call !== latestPreview.current) return;
      (_loginMissing$current2 = loginMissing.current) === null || _loginMissing$current2 === void 0 || _loginMissing$current2.call(loginMissing, refusedForLogins(err));
      const status = err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 ? void 0 : _err$response2.status;
      // A refusal is the server saying these values can't be saved. A 503, no answer, or another change to the
      // company's setups holding its lock is worth asking again.
      const busy = (err === null || err === void 0 || (_err$response3 = err.response) === null || _err$response3 === void 0 || (_err$response3 = _err$response3.data) === null || _err$response3 === void 0 || (_err$response3 = _err$response3.Error) === null || _err$response3 === void 0 ? void 0 : _err$response3.ErrorCode) === SETUP_BUSY;
      const refused = !!status && status < 500 && !busy && !!messageOf(err, null);
      const saysWhy = refused || busy;
      setPreview({
        key,
        state: refused ? 'refused' : 'failed',
        result: null,
        message: saysWhy ? messageOf(err, PREVIEW_FAILED) : PREVIEW_FAILED
      });
    }
  }, [setupId, setupName]);

  // The five values as one string: the preview runs again when one of them changes, never when the name does, and its
  // answer is kept against the values it answered for.
  const valuesKey = draft ? JSON.stringify(valuesOf(draft)) : '';
  useEffect(() => {
    if (!enabled || !open || !setupId || !valuesKey) return undefined;
    runPreview(valuesKey);
    // A late answer after the editor closes says nothing to anyone.
    return () => {
      latestPreview.current += 1;
    };
  }, [enabled, open, setupId, valuesKey, runPreview]);
  if (!enabled || !setup || !draft) return null;

  // A save's refusal was about the draft as it was then.
  const set = (key, value) => {
    setSaveError('');
    setDraft(current => _objectSpread(_objectSpread({}, current), {}, {
      [key]: value
    }));
  };
  const name = draft.name.trim();
  const nameError = name ? '' : 'Give the setup a name.';
  const current = previewFor(preview, valuesKey);
  const result = current.state === 'ready' ? current.result : null;
  const hidden = (_result$impact$hidden = result === null || result === void 0 || (_result$impact = result.impact) === null || _result$impact === void 0 ? void 0 : _result$impact.hiddenProperties) !== null && _result$impact$hidden !== void 0 ? _result$impact$hidden : 0;
  const impact = impactLine({
    setup,
    baseline,
    draft,
    result,
    counting: current.state === 'loading'
  });
  // Save waits for the preview of these values: its answer says whether properties the user can't see hold it. The
  // setup's own values change nothing billed, so the server checks only the name and who may edit: a preview of them
  // that failed, or was refused, doesn't hold a rename, and the server's 403 then judges hidden properties. Any other
  // values wait for a preview that accepts them.
  const answered = current.state !== 'loading';
  const keepsValues = sameValues(draft, setup);
  const previewBlocks = !keepsValues && (current.state === 'refused' || current.state === 'failed');
  const apPush = apPushChoice(setup, allows);
  // On, while the plan doesn't include it (a pending edit from before a downgrade) or isn't known yet: it can only be
  // turned off again.
  const apPushNotAllowed = isAppfolio && draft.isApPushEnabled && apPush.locked;
  const canSave = !saving && !nameError && impact.canSave && answered && !previewBlocks && hidden === 0 && !apPushNotAllowed;
  // The edit scheduled earlier can't apply as it is any more, for example after the company's plan changed.
  const pendingRefused = current.state === 'refused' && !!setup.pendingEdit && sameValues(draft, baseline);
  const methods = methodChoices(setup, occupantsOptIn);
  const submeter = isSubmeterSetup(setup);
  const handleClose = () => {
    if (saving) return;
    onClose === null || onClose === void 0 || onClose();
  };
  const handleSave = async () => {
    if (savingNow.current) return;
    savingNow.current = true;
    const body = _objectSpread(_objectSpread({}, valuesOf(draft)), {}, {
      name
    });
    const changesValues = !sameValues(body, setup);
    setSaving(true);
    setSaveError('');
    onSavingChange === null || onSavingChange === void 0 || onSavingChange(true);
    try {
      var _resp$response;
      const resp = await api.UtilitySetups.editSetup({
        setupId,
        draft: body,
        preview: false
      }, NO_OPTIONS);
      const saved = (_resp$response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response !== void 0 ? _resp$response : null;
      // Applied at once unless the answer names the month it waits for. Every section on the setup then reads it again
      // and hands its values on: they're the server's, so a wrong guess here hands on what the utility already has.
      const appliedNow = changesValues && !(saved !== null && saved !== void 0 && saved.effectiveFrom);
      announceSetupChange(null, {
        appliedSetupId: appliedNow ? setupId : null
      });
      onSaved === null || onSaved === void 0 || onSaved(saved, {
        appliedNow
      });
    } catch (err) {
      var _loginMissing$current3;
      if (refusedForLogins(err)) (_loginMissing$current3 = loginMissing.current) === null || _loginMissing$current3 === void 0 || _loginMissing$current3.call(loginMissing, true);
      setSaveError(messageOf(err, 'Could not save the setup.'));
    } finally {
      savingNow.current = false;
      setSaving(false);
      onSavingChange === null || onSavingChange === void 0 || onSavingChange(false);
    }
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: open,
    handleClose: handleClose,
    variant: "middle",
    title: "Edit ".concat(setup.name),
    leftButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleClose,
      variant: "text",
      disabled: saving
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleSave,
      size: "small",
      variant: "contained",
      disabled: !canSave
    }, "Save")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editor,
    "data-testid": "utility-setup-editor"
  }, setup.pendingEdit && /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "This shows the edit scheduled from ", dayLabel(setup.pendingEdit.effectiveFrom), ". Saving replaces it."), /*#__PURE__*/React.createElement(TextField, {
    id: "setup-name-".concat(setup.id),
    size: "small",
    label: "Setup name",
    value: draft.name,
    onChange: event => set('name', event.target.value),
    inputProps: {
      maxLength: MAX_SETUP_NAME_LENGTH
    },
    error: !!nameError,
    helperText: nameError || ' ',
    disabled: saving
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    component: "div",
    className: classes.heading,
    id: "setup-source-".concat(setup.id)
  }, "How do bills reach Livable?", /*#__PURE__*/React.createElement(Tip, {
    label: "How bills reach Livable",
    text: SETUP_TIPS.source
  })), /*#__PURE__*/React.createElement(RadioGroup, {
    "aria-labelledby": "setup-source-".concat(setup.id),
    value: (_draft$billSource = draft.billSource) !== null && _draft$billSource !== void 0 ? _draft$billSource : '',
    onChange: event => set('billSource', event.target.value),
    className: classes.cards
  }, SOURCE_CHOICES.map(choice => {
    const _sourceChoice = sourceChoice(choice.value, setup, allows),
      needs = _sourceChoice.needs,
      locked = _sourceChoice.locked;
    return /*#__PURE__*/React.createElement(SourceCard, {
      key: choice.value,
      choice: choice,
      selected: draft.billSource === choice.value,
      locked: locked || saving,
      needs: needs,
      classes: classes
    });
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: "__mail",
    disabled: true,
    className: classes.card,
    control: /*#__PURE__*/React.createElement(Radio, {
      size: "small"
    }),
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.cardText
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      component: "span",
      className: classes.cardTitle
    }, MAIL_CHOICE.title, " ", /*#__PURE__*/React.createElement("span", {
      className: classes.soon
    }, "Coming soon")), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      component: "span",
      color: "light"
    }, MAIL_CHOICE.sub))
  })), draft.billSource === 'Email' && /*#__PURE__*/React.createElement(IntakeAddress, {
    className: classes.intakeBox
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.heading
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: draft.billToResidents,
      disabled: true
    }),
    label: "Bill to residents"
  }), /*#__PURE__*/React.createElement(Tip, {
    label: "Bill to residents",
    text: SETUP_TIPS.billToResidents
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Turning it off comes in Phase 2.")), /*#__PURE__*/React.createElement("div", {
    className: classes.twin
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "setup-method-".concat(setup.id),
    size: "small",
    label: "Allocation method",
    value: draft.allocationMethod,
    onChange: event => set('allocationMethod', event.target.value),
    disabled: submeter || saving
  }, methods.map(method => /*#__PURE__*/React.createElement(MenuItem, {
    key: method,
    value: method
  }, methodLabel(method)))), submeter && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "A Sub-meter setup stays Sub-meter."), SQUARE_FOOTAGE_METHODS.includes(draft.allocationMethod) && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Each property needs its square footage set.")), /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.heading
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "setup-frequency-".concat(setup.id),
    size: "small",
    label: "Bill frequency",
    value: draft.billFrequencyType,
    onChange: event => set('billFrequencyType', event.target.value),
    disabled: saving,
    className: classes.grow
  }, FREQUENCY_CHOICES.map(frequency => /*#__PURE__*/React.createElement(MenuItem, {
    key: frequency,
    value: frequency
  }, frequencyLabel(frequency)))), /*#__PURE__*/React.createElement(Tip, {
    label: "Bill frequency",
    text: SETUP_TIPS.frequency
  })))), isAppfolio && /*#__PURE__*/React.createElement("div", {
    className: classes.heading
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: draft.isApPushEnabled,
      onChange: event => set('isApPushEnabled', event.target.checked),
      disabled: apPush.locked && !draft.isApPushEnabled || saving
    }),
    label: "AP Push"
  }), /*#__PURE__*/React.createElement(Tip, {
    label: "AP Push",
    text: SETUP_TIPS.apPush
  }), apPush.needs ? /*#__PURE__*/React.createElement(Needs, {
    tier: apPush.needs,
    className: classes.needs
  }) : null, apPush.planUnknown ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "span"
  }, "Turning it on waits for the company's plan.") : null, apPushNotAllowed && !apPush.planUnknown ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "error",
    component: "span"
  }, "Your plan doesn't include AP Push: turn it off to save.") : null), impact.text && /*#__PURE__*/React.createElement("div", {
    className: classes.impact,
    role: "status"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, impact.text)), current.state === 'refused' && /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, current.message, pendingRefused ? " The edit scheduled earlier can't apply as it is: change it here, or cancel it from the Setup section." : ''), current.state === 'failed' && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => runPreview(valuesKey)
    }, "Retry")
  }, current.message), hidden > 0 && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, "This setup also drives ", hidden, " ", hidden === 1 ? 'property' : 'properties', " you can't see. Someone with access to all of them can change it."), saveError && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    onClose: () => setSaveError('')
  }, saveError)));
});